import { useCallback, useEffect, useLayoutEffect } from "react";
import { useResizablePanel } from "./useResizablePanel";

export const CODE_PANE_MIN_WIDTH = 320;
export const DIAGRAM_MIN_WIDTH = 480;

export function clampSplitPaneWidth(
  containerWidth,
  requestedWidth,
  minLeading = CODE_PANE_MIN_WIDTH,
  minTrailing = DIAGRAM_MIN_WIDTH
) {
  const maxWidth = Math.max(0, containerWidth - minTrailing);
  const minWidth = Math.min(minLeading, maxWidth);
  return Math.min(maxWidth, Math.max(minWidth, requestedWidth));
}

export function useSplitPaneResize({
  containerRef,
  enabled,
  defaultRatio = 0.5,
  minLeading = CODE_PANE_MIN_WIDTH,
  minTrailing = DIAGRAM_MIN_WIDTH,
}) {
  const resolveContainerWidth = useCallback(
    () => containerRef.current?.clientWidth ?? 0,
    [containerRef]
  );
  const resolveMaxWidth = useCallback(
    () => Math.max(0, resolveContainerWidth() - minTrailing),
    [minTrailing, resolveContainerWidth]
  );
  const resolveMinWidth = useCallback(
    () => Math.min(minLeading, resolveMaxWidth()),
    [minLeading, resolveMaxWidth]
  );
  const [leadingWidth, onResizeStart, setLeadingWidth] = useResizablePanel(
    null,
    resolveMinWidth,
    resolveMaxWidth
  );

  const resizeLeadingPane = useCallback(
    (requestedWidth) => {
      const width = resolveContainerWidth();
      if (!width) return;
      setLeadingWidth(
        clampSplitPaneWidth(width, requestedWidth, minLeading, minTrailing)
      );
    },
    [minLeading, minTrailing, resolveContainerWidth, setLeadingWidth]
  );

  useLayoutEffect(() => {
    if (!enabled || leadingWidth !== null) return;
    const width = containerRef.current?.clientWidth ?? 0;
    if (width) resizeLeadingPane(width * defaultRatio);
  }, [defaultRatio, enabled, leadingWidth, resizeLeadingPane]);

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      setLeadingWidth((current) =>
        clampSplitPaneWidth(
          width,
          current ?? width * defaultRatio,
          minLeading,
          minTrailing
        )
      );
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, defaultRatio, enabled, minLeading, minTrailing]);

  return { leadingWidth, onResizeStart };
}
